/**
 * Native date range / single date picker — Tailwind light/dark + responsive.
 * jQuery-compatible: $(el).daterangepicker(options, cb) + apply.daterangepicker
 */
(function (global) {
  const DAY_MS = 86400000;

  const DEFAULT_LOCALE = {
    applyLabel: "Apply",
    cancelLabel: "Cancel",
    fromLabel: "From",
    toLabel: "To",
    customRangeLabel: "Custom",
    daysOfWeek: ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"],
    monthNames: [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ],
    format: "MM/DD/YYYY",
  };

  function pad(n) {
    return String(n).padStart(2, "0");
  }

  function startOfDay(d) {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x;
  }

  function endOfDay(d) {
    const x = new Date(d);
    x.setHours(23, 59, 59, 999);
    return x;
  }

  function sameDay(a, b) {
    return (
      a &&
      b &&
      a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate()
    );
  }

  function clampDate(d, min, max) {
    let x = startOfDay(d);
    if (min && x < min) x = new Date(min);
    if (max && x > max) x = new Date(max);
    return x;
  }

  function toDate(value) {
    if (value == null || value === "") return null;
    if (value instanceof Date && !isNaN(value)) return startOfDay(value);
    if (typeof value.toDate === "function") {
      try {
        return startOfDay(value.toDate());
      } catch (_) {
        /* ignore */
      }
    }
    if (typeof value.valueOf === "function" && typeof value === "object") {
      const n = Number(value.valueOf());
      if (!isNaN(n) && n > 0) return startOfDay(new Date(n));
    }
    if (typeof value === "string") {
      const mdy = value.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})/);
      if (mdy) {
        let y = parseInt(mdy[3], 10);
        if (y < 100) y += 2000;
        return startOfDay(new Date(y, parseInt(mdy[1], 10) - 1, parseInt(mdy[2], 10)));
      }
      const ymd = value.match(/^(\d{4})[\/\-](\d{1,2})[\/\-](\d{1,2})/);
      if (ymd) {
        return startOfDay(
          new Date(parseInt(ymd[1], 10), parseInt(ymd[2], 10) - 1, parseInt(ymd[3], 10))
        );
      }
      const parsed = Date.parse(value);
      if (!isNaN(parsed)) return startOfDay(new Date(parsed));
    }
    return null;
  }

  function formatDate(date, format) {
    if (!date) return "";
    const map = {
      YYYY: String(date.getFullYear()),
      YY: String(date.getFullYear()).slice(-2),
      MM: pad(date.getMonth() + 1),
      M: String(date.getMonth() + 1),
      DD: pad(date.getDate()),
      D: String(date.getDate()),
      HH: pad(date.getHours()),
      mm: pad(date.getMinutes()),
      ii: pad(date.getMinutes()),
    };
    let out = format || DEFAULT_LOCALE.format;
    return out.replace(/YYYY|YY|MM|M|DD|D|HH|mm|ii/g, (t) => map[t]);
  }

  function dateToken(date) {
    return date
      ? {
          _d: date,
          format(fmt) {
            return formatDate(date, fmt || DEFAULT_LOCALE.format);
          },
          toDate() {
            return new Date(date);
          },
          valueOf() {
            return date.getTime();
          },
        }
      : null;
  }

  function addMonths(date, n) {
    const d = new Date(date);
    d.setDate(1);
    d.setMonth(d.getMonth() + n);
    return startOfDay(d);
  }

  function daysInMonth(year, month) {
    return new Date(year, month + 1, 0).getDate();
  }

  function parseRangeValue(str, format) {
    if (!str || typeof str !== "string") return null;
    const parts = str.split(/\s+-\s+/);
    if (parts.length !== 2) {
      const single = toDate(str.trim());
      return single ? { start: single, end: single } : null;
    }
    const start = toDate(parts[0].trim());
    const end = toDate(parts[1].trim());
    if (!start || !end) return null;
    return { start, end: end < start ? start : end };
  }

  class DateRangePicker {
    constructor(element, options = {}, callback) {
      this.element = element;
      this.callback = typeof callback === "function" ? callback : null;
      this.options = options;
      this.locale = { ...DEFAULT_LOCALE, ...(options.locale || {}) };
      this.single = !!options.singleDatePicker;
      this.autoApply = options.autoApply !== false && (this.single || !!options.autoApply);
      this.autoUpdateInput = options.autoUpdateInput !== false;
      this.timePicker = !!options.timePicker;
      this.showDropdowns = !!options.showDropdowns;

      this.minDate = toDate(options.minDate);
      // Only apply maxDate when explicitly configured — otherwise any future date is allowed
      this.maxDate = toDate(options.maxDate);
      this.opens = options.opens === "right" ? "right" : "left";
      this.drops = options.drops === "up" ? "up" : "down";

      const parsed = parseRangeValue(element.value, this.locale.format);
      const optStart = toDate(options.startDate);
      const optEnd = toDate(options.endDate);
      this.startDate = clampDate(
        optStart || (parsed && parsed.start) || startOfDay(new Date()),
        this.minDate,
        this.maxDate
      );
      this.endDate = this.single
        ? new Date(this.startDate)
        : clampDate(
            optEnd || (parsed && parsed.end) || startOfDay(new Date()),
            this.minDate,
            this.maxDate
          );

      if (!optStart && !parsed && !this.single) {
        this.startDate = clampDate(startOfDay(new Date(Date.now() - 13 * DAY_MS)), this.minDate, this.maxDate);
        this.endDate = clampDate(startOfDay(new Date()), this.minDate, this.maxDate);
      }

      // Preserve time from option/value when timePicker is on
      if (this.timePicker) {
        const raw = options.startDate;
        let src = null;
        if (raw && typeof raw.toDate === "function") src = raw.toDate();
        else if (raw instanceof Date) src = raw;
        else if (element.value) {
          const p = Date.parse(element.value);
          if (!isNaN(p)) src = new Date(p);
        }
        if (src) {
          this.startDate = new Date(this.startDate);
          this.startDate.setHours(src.getHours(), src.getMinutes(), 0, 0);
          this.endDate = new Date(this.startDate);
        }
      }

      this.hoverDate = null;
      this.viewDate = startOfDay(new Date(this.endDate || this.startDate));
      this.viewDate.setDate(1);
      this.picking = null;
      this.open = false;
      this.activeRange = null;

      this.ranges = [];
      if (options.ranges && typeof options.ranges === "object") {
        Object.keys(options.ranges).forEach((label) => {
          const pair = options.ranges[label];
          if (!pair || !pair.length) return;
          const start = toDate(pair[0]);
          const end = toDate(pair[1]);
          if (!start || !end) return;
          this.ranges.push({
            label,
            start: clampDate(start, this.minDate, this.maxDate),
            end: clampDate(end, this.minDate, this.maxDate),
          });
        });
      }

      this._onDocDown = (e) => {
        if (!this.open || this._ignoreDocDown) return;
        if (this.panel.contains(e.target) || this.element.contains(e.target)) return;
        if (e.target && e.target.closest && e.target.closest("[data-drp-panel]")) return;
        this.hide();
      };
      this._onKey = (e) => {
        if (e.key === "Escape" && this.open) {
          e.stopPropagation();
          this.hide();
        }
      };
      this._onReposition = () => {
        if (this.open) this._position();
      };

      this._build();
      this._bind();
      if (this.autoUpdateInput) this._writeInput(false);
      element._nativeDateRangePicker = this;
    }

    _isCompact() {
      return this.single && !this.ranges.length;
    }

    _build() {
      this.backdrop = document.createElement("div");
      this.backdrop.className =
        "fixed inset-0 z-[1200] hidden bg-black/40 dark:bg-black/60 sm:bg-transparent sm:dark:bg-transparent";
      this.backdrop.setAttribute("data-drp-backdrop", "");

      const compact = this._isCompact();
      this.panel = document.createElement("div");
      // Above portaled dropdown menus (inline z-index 1100 in ui.js)
      this.panel.className = compact
        ? "fixed inset-x-0 bottom-0 z-[1201] hidden max-h-[85vh] w-full overflow-auto rounded-t-xl border border-black/10 bg-white p-3 shadow-xl dark:border-white/10 dark:bg-neutral-900 sm:absolute sm:inset-auto sm:bottom-auto sm:left-0 sm:right-auto sm:mt-1 sm:max-h-[min(24rem,calc(100vh-1rem))] sm:w-max sm:max-w-[min(17.5rem,calc(100vw-1rem))] sm:min-w-[16rem] sm:rounded-lg sm:p-3"
        : "fixed inset-x-0 bottom-0 z-[1201] hidden max-h-[92vh] w-full overflow-auto rounded-t-2xl border border-black/10 bg-white p-3 shadow-2xl dark:border-white/10 dark:bg-neutral-900 sm:absolute sm:inset-auto sm:bottom-auto sm:left-0 sm:right-auto sm:mt-1.5 sm:max-h-[min(28rem,calc(100vh-1rem))] sm:w-auto sm:max-w-[min(36rem,calc(100vw-1rem))] sm:min-w-[20rem] sm:rounded-xl sm:p-4";
      this.panel.setAttribute("role", "dialog");
      this.panel.setAttribute("aria-modal", "true");
      this.panel.setAttribute("data-drp-panel", "");

      // Always portal to body so the panel stacks above modal backdrops
      // (modals create a z-1055 context that would otherwise trap an in-flow panel).
      document.body.appendChild(this.backdrop);
      document.body.appendChild(this.panel);

      this.element.setAttribute("autocomplete", "off");
      this.element.setAttribute("readonly", "readonly");
    }

    _bind() {
      this.element.addEventListener("click", (e) => {
        e.preventDefault();
        this.toggle();
      });
      this.element.addEventListener("focus", () => {
        if (!this.open) this.show();
      });
      this.backdrop.addEventListener("mousedown", (e) => e.stopPropagation());
      this.backdrop.addEventListener("click", (e) => {
        e.stopPropagation();
        this.hide();
      });
      // Keep modal dismiss / outside-click handlers from seeing picker events
      const stop = (e) => e.stopPropagation();
      this.panel.addEventListener("mousedown", stop);
      this.panel.addEventListener("click", stop);
      this.panel.addEventListener("touchstart", stop, { passive: true });
    }

    toggle() {
      this.open ? this.hide() : this.show();
    }

    show() {
      if (this.open) return;
      this.open = true;
      this.picking = null;
      this.hoverDate = null;
      this.draftStart = new Date(this.startDate);
      this.draftEnd = new Date(this.endDate);
      this.viewDate = startOfDay(new Date(this.draftEnd || this.draftStart));
      this.viewDate.setDate(1);
      this._render();
      this.panel.classList.remove("hidden");
      this.backdrop.classList.remove("hidden");
      document.addEventListener("mousedown", this._onDocDown, true);
      document.addEventListener("keydown", this._onKey, true);
      document.addEventListener("scroll", this._onReposition, true);
      window.addEventListener("resize", this._onReposition);
      this._position();
      if (global.jQuery) {
        global.jQuery(this.element).trigger("show.daterangepicker", this._pickerApi());
      }
    }

    hide() {
      if (!this.open) return;
      this.open = false;
      this.panel.classList.add("hidden");
      this.backdrop.classList.add("hidden");
      document.removeEventListener("mousedown", this._onDocDown, true);
      document.removeEventListener("keydown", this._onKey, true);
      document.removeEventListener("scroll", this._onReposition, true);
      window.removeEventListener("resize", this._onReposition);
      if (global.jQuery) {
        global.jQuery(this.element).trigger("hide.daterangepicker", this._pickerApi());
      }
    }

    remove() {
      this.hide();
      this.panel.remove();
      this.backdrop.remove();
      delete this.element._nativeDateRangePicker;
    }

    _position() {
      if (global.matchMedia("(max-width: 639px)").matches) {
        this.panel.style.position = "";
        this.panel.style.left = "";
        this.panel.style.right = "";
        this.panel.style.top = "";
        this.panel.style.bottom = "";
        this.panel.style.marginTop = "";
        this.panel.style.marginBottom = "";
        this.panel.style.transform = "";
        return;
      }

      const anchor = this.element.getBoundingClientRect();
      const openLeft = this.opens !== "right";
      const preferUp = this.drops === "up";

      this.panel.style.position = "fixed";
      this.panel.style.marginTop = "";
      this.panel.style.marginBottom = "";
      this.panel.style.transform = "";
      this.panel.style.right = "auto";
      this.panel.style.bottom = "auto";
      this.panel.style.top = `${Math.round(anchor.bottom + 4)}px`;
      this.panel.style.left = `${Math.round(openLeft ? anchor.left : anchor.right)}px`;

      requestAnimationFrame(() => {
        if (!this.open) return;
        const pr = this.panel.getBoundingClientRect();
        const pad = 8;
        const gap = 4;
        const vw = global.innerWidth;
        const vh = global.innerHeight;
        const spaceBelow = vh - anchor.bottom - pad;
        const spaceAbove = anchor.top - pad;
        const placeUp = preferUp || (pr.height > spaceBelow && spaceAbove > spaceBelow);

        let top = placeUp ? anchor.top - pr.height - gap : anchor.bottom + gap;
        top = Math.min(Math.max(pad, top), Math.max(pad, vh - pr.height - pad));

        let left = openLeft ? anchor.left : anchor.right - pr.width;
        left = Math.min(Math.max(pad, left), Math.max(pad, vw - pr.width - pad));

        this.panel.style.top = `${Math.round(top)}px`;
        this.panel.style.left = `${Math.round(left)}px`;
      });
    }

    _pickerApi() {
      return {
        startDate: dateToken(this.startDate),
        endDate: dateToken(this.endDate),
        element: this.element,
      };
    }

    _writeInput(triggerApply) {
      let value;
      if (this.single) {
        const fmt =
          this.locale.format ||
          (this.timePicker ? "YYYY-MM-DD HH:mm" : "YYYY-MM-DD");
        value = formatDate(this.startDate, fmt);
      } else {
        value =
          formatDate(this.startDate, "MM/DD/YYYY") +
          " - " +
          formatDate(this.endDate, "MM/DD/YYYY");
      }
      if (this.autoUpdateInput) {
        this.element.value = value;
      }

      if (this.callback) {
        this.callback(dateToken(this.startDate), dateToken(this.endDate));
      }
      if (triggerApply && global.jQuery) {
        global.jQuery(this.element).trigger("apply.daterangepicker", this._pickerApi());
      }
      this.element.dispatchEvent(
        new CustomEvent("apply.daterangepicker", {
          bubbles: true,
          detail: this._pickerApi(),
        })
      );
    }

    _syncTimeFromInputs() {
      if (!this.timePicker) return;
      const hour = parseInt(this.panel.querySelector("[data-drp-hour]")?.value || "0", 10);
      const minute = parseInt(this.panel.querySelector("[data-drp-minute]")?.value || "0", 10);
      this.draftStart = new Date(this.draftStart);
      this.draftStart.setHours(hour, minute, 0, 0);
      this.draftEnd = new Date(this.draftStart);
    }

    _apply() {
      this._syncTimeFromInputs();
      if (this.timePicker) {
        this.startDate = new Date(this.draftStart);
        this.endDate = new Date(this.draftEnd || this.draftStart);
      } else {
        this.startDate = startOfDay(this.draftStart);
        this.endDate = startOfDay(this.draftEnd || this.draftStart);
      }
      if (this.endDate < this.startDate) {
        const t = this.startDate;
        this.startDate = this.endDate;
        this.endDate = t;
      }
      this._writeInput(true);
      this.hide();
    }

    _cancel() {
      if (global.jQuery) {
        global.jQuery(this.element).trigger("cancel.daterangepicker", this._pickerApi());
      }
      this.hide();
    }

    _selectDay(day) {
      this._ignoreDocDown = true;
      const release = () => {
        this._ignoreDocDown = false;
      };
      setTimeout(release, 0);

      const date = clampDate(day, this.minDate, this.maxDate);
      if (this.single) {
        const hours = this.draftStart?.getHours?.() || 0;
        const minutes = this.draftStart?.getMinutes?.() || 0;
        this.draftStart = new Date(date);
        if (this.timePicker) this.draftStart.setHours(hours, minutes, 0, 0);
        this.draftEnd = new Date(this.draftStart);
        if (this.autoApply && !this.timePicker) {
          this._apply();
          return;
        }
        this._render();
        return;
      }

      if (!this.picking || this.picking === "end") {
        this.draftStart = date;
        this.draftEnd = date;
        this.picking = "start";
        this.activeRange = null;
        this._render();
        return;
      }

      if (date < this.draftStart) {
        this.draftEnd = this.draftStart;
        this.draftStart = date;
      } else {
        this.draftEnd = date;
      }
      this.picking = "end";
      this.activeRange = null;
      if (this.autoApply && !this.timePicker) {
        this._apply();
        return;
      }
      this._render();
    }

    _setRange(range) {
      this.draftStart = new Date(range.start);
      this.draftEnd = new Date(range.end);
      this.picking = "end";
      this.activeRange = range.label;
      this.viewDate = startOfDay(new Date(range.end));
      this.viewDate.setDate(1);
      if (this.autoApply || true) {
        // Presets apply immediately (matches original UX)
        this._apply();
      }
    }

    _inRange(day) {
      if (!this.draftStart || !this.draftEnd) return false;
      const a = this.draftStart < this.draftEnd ? this.draftStart : this.draftEnd;
      const b = this.draftStart < this.draftEnd ? this.draftEnd : this.draftStart;
      return day >= a && day <= b;
    }

    _render() {
      const L = this.locale;
      const year = this.viewDate.getFullYear();
      const month = this.viewDate.getMonth();
      const compact = this._isCompact();
      const canPrev =
        !this.minDate || addMonths(this.viewDate, 0) > startOfDay(new Date(this.minDate.getFullYear(), this.minDate.getMonth(), 1));
      const nextMonth = addMonths(this.viewDate, 1);
      const canNext = !this.maxDate || nextMonth <= this.maxDate;

      const cell = compact ? "h-8 w-8" : "h-9 w-9";
      const navBtn = compact ? "size-8" : "size-9";
      const navIcon = compact ? "size-4" : "size-4";

      const rangesHtml = this.ranges.length
        ? `<div class="mb-3 flex gap-1.5 overflow-x-auto pb-1 sm:mb-0 sm:block sm:w-40 sm:shrink-0 sm:space-y-0.5 sm:overflow-visible sm:border-r sm:border-black/5 sm:pr-3 sm:dark:border-white/10">
            ${this.ranges
              .map(
                (r) => `<button type="button" data-drp-range="${escapeAttr(r.label)}" class="shrink-0 rounded-md px-2 py-1 text-left text-[11px] font-medium transition hover:bg-black/4 dark:hover:bg-white/6 sm:w-full ${
                  this.activeRange === r.label
                    ? "bg-black text-white dark:bg-white dark:text-black"
                    : "text-black/70 dark:text-white/70"
                }">${escapeHtml(r.label)}</button>`
              )
              .join("")}
          </div>`
        : "";

      const weekHead = L.daysOfWeek
        .map(
          (d) =>
            `<div class="flex ${compact ? "h-7" : "h-8"} items-center justify-center text-[0.6rem] font-semibold uppercase tracking-wide text-black/35 dark:text-white/35">${escapeHtml(d)}</div>`
        )
        .join("");

      const firstDow = new Date(year, month, 1).getDay();
      const dim = daysInMonth(year, month);
      const cells = [];
      for (let i = 0; i < firstDow; i++) {
        cells.push(`<div class="${cell}"></div>`);
      }
      for (let day = 1; day <= dim; day++) {
        const date = startOfDay(new Date(year, month, day));
        const disabled =
          (this.minDate && date < this.minDate) || (this.maxDate && date > this.maxDate);
        const isStart = sameDay(date, this.draftStart);
        const isEnd = sameDay(date, this.draftEnd);
        const inRange = !this.single && this._inRange(date);
        const isToday = sameDay(date, new Date());

        let cls =
          `relative flex ${cell} items-center justify-center rounded-md text-[11px] font-medium transition focus:outline-none focus-visible:ring-2 focus-visible:ring-black/20 dark:focus-visible:ring-white/25 `;
        if (disabled) {
          cls += "cursor-not-allowed text-black/20 dark:text-white/20";
        } else if (isStart || isEnd) {
          cls += "bg-black font-semibold text-white dark:bg-white dark:text-black";
        } else if (inRange) {
          cls += "bg-black/6 text-black dark:bg-white/[0.1] dark:text-white";
        } else {
          cls += "text-black/80 hover:bg-black/4 dark:text-white/80 dark:hover:bg-white/6";
        }
        if (isToday && !isStart && !isEnd) {
          cls += " ring-1 ring-inset ring-black/15 dark:ring-white/20";
        }

        cells.push(
          `<button type="button" data-drp-day="${date.getTime()}" class="${cls}" ${disabled ? "disabled" : ""}>${day}</button>`
        );
      }

      const pretty = (d) =>
        d
          ? `${L.monthNames[d.getMonth()].slice(0, 3)} ${d.getDate()}, ${d.getFullYear()}`
          : "";
      const summary = this.single
        ? pretty(this.draftStart)
        : `${pretty(this.draftStart)} – ${pretty(this.draftEnd || this.draftStart)}`;

      this.panel.innerHTML = `
        <div class="flex flex-col ${compact ? "gap-2" : "gap-3 sm:flex-row sm:gap-4"}">
          ${rangesHtml}
          <div class="min-w-0 flex-1 ${compact ? "" : "sm:min-w-[17rem]"}">
            <div class="${compact ? "mb-2" : "mb-2.5"} flex items-center justify-between gap-1">
              <button type="button" data-drp-prev class="inline-flex ${navBtn} items-center justify-center rounded-md text-black/50 transition hover:bg-black/4 hover:text-black disabled:opacity-30 dark:text-white/50 dark:hover:bg-white/6 dark:hover:text-white" ${canPrev ? "" : "disabled"} aria-label="Previous month">
                <svg class="${navIcon}" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5"/></svg>
              </button>
              <div class="${compact ? "text-xs" : "text-sm"} font-semibold tracking-tight">${escapeHtml(L.monthNames[month])} ${year}</div>
              <button type="button" data-drp-next class="inline-flex ${navBtn} items-center justify-center rounded-md text-black/50 transition hover:bg-black/4 hover:text-black disabled:opacity-30 dark:text-white/50 dark:hover:bg-white/6 dark:hover:text-white" ${canNext ? "" : "disabled"} aria-label="Next month">
                <svg class="${navIcon}" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5"/></svg>
              </button>
            </div>
            <div class="grid grid-cols-7 justify-items-center gap-0.5">${weekHead}${cells.join("")}</div>
            ${
              this.timePicker
                ? `<div class="${compact ? "mt-2" : "mt-3"} flex items-center gap-1.5">
              <label class="text-[0.6rem] font-medium text-black/45 dark:text-white/45">Time</label>
              <select data-drp-hour class="rounded-md border border-black/10 bg-white px-1.5 py-0.5 text-[11px] dark:border-white/10 dark:bg-neutral-950">
                ${Array.from({ length: 24 }, (_, h) => `<option value="${h}" ${this.draftStart.getHours() === h ? "selected" : ""}>${pad(h)}</option>`).join("")}
              </select>
              <span class="text-[11px] text-black/40 dark:text-white/40">:</span>
              <select data-drp-minute class="rounded-md border border-black/10 bg-white px-1.5 py-0.5 text-[11px] dark:border-white/10 dark:bg-neutral-950">
                ${Array.from({ length: 60 }, (_, m) => `<option value="${m}" ${this.draftStart.getMinutes() === m ? "selected" : ""}>${pad(m)}</option>`).join("")}
              </select>
            </div>`
                : ""
            }
            <div class="${compact ? "mt-2.5 pt-2.5" : "mt-3 pt-3"} flex flex-col gap-2 border-t border-black/5 dark:border-white/10 sm:flex-row sm:items-center sm:justify-between">
              <p class="text-[11px] text-black/45 dark:text-white/45">${escapeHtml(summary)}</p>
              <div class="flex items-center justify-start gap-1.5 sm:justify-end">
                <button type="button" data-drp-cancel class="inline-flex items-center justify-center rounded-md border border-black/10 px-2.5 py-1 text-[11px] font-medium transition hover:bg-black/4 dark:border-white/10 dark:hover:bg-white/6">${escapeHtml(L.cancelLabel)}</button>
                <button type="button" data-drp-apply class="inline-flex items-center justify-center rounded-md bg-black px-2.5 py-1 text-[11px] font-semibold text-white transition hover:bg-black/85 dark:bg-white dark:text-black dark:hover:bg-white/90">${escapeHtml(L.applyLabel)}</button>
              </div>
            </div>
          </div>
        </div>`;

      this.panel.querySelector("[data-drp-prev]")?.addEventListener("click", () => {
        this.viewDate = addMonths(this.viewDate, -1);
        this._render();
      });
      this.panel.querySelector("[data-drp-next]")?.addEventListener("click", () => {
        this.viewDate = addMonths(this.viewDate, 1);
        this._render();
      });
      this.panel.querySelector("[data-drp-cancel]")?.addEventListener("click", () => this._cancel());
      this.panel.querySelector("[data-drp-apply]")?.addEventListener("click", () => {
        this._syncTimeFromInputs();
        this._apply();
      });

      this.panel.querySelectorAll("[data-drp-day]").forEach((btn) => {
        const ts = parseInt(btn.getAttribute("data-drp-day"), 10);
        btn.addEventListener("click", () => this._selectDay(new Date(ts)));
      });

      this.panel.querySelector("[data-drp-hour]")?.addEventListener("change", () => {
        this._syncTimeFromInputs();
      });
      this.panel.querySelector("[data-drp-minute]")?.addEventListener("change", () => {
        this._syncTimeFromInputs();
      });

      this.panel.querySelectorAll("[data-drp-range]").forEach((btn) => {
        btn.addEventListener("click", () => {
          const label = btn.getAttribute("data-drp-range");
          const range = this.ranges.find((r) => r.label === label);
          if (range) this._setRange(range);
        });
      });
    }
  }

  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  function escapeAttr(str) {
    return escapeHtml(str).replace(/'/g, "&#39;");
  }

  function attach(element, options, callback) {
    if (!element) return null;
    if (element._nativeDateRangePicker) {
      element._nativeDateRangePicker.remove();
    }
    return new DateRangePicker(element, options, callback);
  }

  function daysAgo(n, from) {
    const d = startOfDay(from || new Date());
    d.setDate(d.getDate() - n);
    return d;
  }

  /**
   * Bind report-style range pickers (replaces jQuery + Moment inits).
   * config: { locale, ranges:{last7,last30,thisMonth,lastMonth,last3,last12?}, minDate?, includeLast12? }
   */
  function bindReport(selector, config = {}) {
    const today = startOfDay(new Date());
    const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const labels = config.ranges || {};
    const ranges = {};

    if (labels.last7) ranges[labels.last7] = [daysAgo(6, today), today];
    if (labels.last30) ranges[labels.last30] = [daysAgo(29, today), today];
    if (labels.thisMonth) {
      ranges[labels.thisMonth] = [
        new Date(today.getFullYear(), today.getMonth(), 1),
        new Date(today.getFullYear(), today.getMonth() + 1, 0),
      ];
    }
    if (labels.lastMonth) {
      ranges[labels.lastMonth] = [
        new Date(lastMonth.getFullYear(), lastMonth.getMonth(), 1),
        new Date(lastMonth.getFullYear(), lastMonth.getMonth() + 1, 0),
      ];
    }
    if (labels.last3) {
      ranges[labels.last3] = [
        new Date(today.getFullYear(), today.getMonth() - 2, 1),
        today,
      ];
    }
    if ((config.includeLast12 || labels.last12) && labels.last12) {
      ranges[labels.last12] = [
        new Date(today.getFullYear(), today.getMonth() - 12, 1),
        today,
      ];
    }

    const opts = {
      locale: { ...DEFAULT_LOCALE, ...(config.locale || {}) },
      maxDate: today,
      startDate: daysAgo(14, today),
      endDate: today,
      autoUpdateInput: true,
      ranges,
    };
    if (config.minDate) opts.minDate = config.minDate;

    const run = () => {
      document.querySelectorAll(selector).forEach((el) => attach(el, opts));
    };
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", run);
    } else {
      run();
    }
  }

  function installJQuery($) {
    if (!$ || !$.fn || $.fn.daterangepicker?.__native) return;
    $.fn.daterangepicker = function (options, callback) {
      return this.each(function () {
        attach(this, options || {}, callback);
      });
    };
    $.fn.daterangepicker.__native = true;
  }

  if (global.jQuery) installJQuery(global.jQuery);

  global.NativeDateRangePicker = {
    attach,
    bindReport,
    installJQuery,
    DateRangePicker,
  };
})(typeof window !== "undefined" ? window : globalThis);
